import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { HOSTNAME, LargeDataAnalyticsHostname } from '../../Configs/Domain';
import {generateRandomColor} from "../../utils/colorUtils";


export const getAllocationHistory = createAsyncThunk(
  'timeSeriesModels/getAllocationHistory',
  async ({ pid, startDate, endDate }, { getState, rejectWithValue }) => {
    try {
      let url = `${HOSTNAME}/performance/allocation/${pid}?`;
      if (!startDate) {
        startDate = '2018-01-01';
      }
      url += `sd=${startDate}&`;
      if (endDate) {
        url += `ed=${endDate}&`;
      }
      url = url.slice(0, -1);

      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      return { pid, data };
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const fetchIncomeModel = createAsyncThunk(
  'timeSeriesModels/getIncomeModel',
  async ({ timeseries_id, name, startDate, initial_capital, ppy, withdrawal_rate, min_withdrawal_amount }, { rejectWithValue }) => {
    try {
      let url = `${LargeDataAnalyticsHostname}/models/income/${timeseries_id}?`;
      url += `initial_capital=${initial_capital}&`;
      url += `ppy=${ppy}&`;
      url += `withdrawal_rate=${withdrawal_rate}&`;
      url += `min_withdrawal_amount=${min_withdrawal_amount}`;
      if (startDate) {
            url += `&start_date=${startDate}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      return { id: Date.now(), ...data, name, timeseriesId: timeseries_id, color: generateRandomColor({existingColors: []})} ;  // Add a unique id to each model
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

const initialState = {
  allocation: {},
  incomeModels: [],
  loading: 'idle',
  error: null,
};

const timeSeriesModelsSlice = createSlice({
  name: 'timeSeriesModels',
  initialState,
  reducers: {
    resetIncomeModels: (state) => {
      state.incomeModels = [];
    },
    removeIncomeModel: (state, action) => {
      state.incomeModels = state.incomeModels.filter(model => model.id !== action.payload);
    },
    updateModelColor: (state, action) => {
      const { id, color } = action.payload;
      const modelIndex = state.incomeModels.findIndex(model => model.id === id);
      if (modelIndex !== -1) {
        state.incomeModels[modelIndex].color = color;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllocationHistory.pending, (state) => {
        state.loading = 'loading';
      })
      .addCase(getAllocationHistory.fulfilled, (state, action) => {
        const { pid, data } = action.payload;
        state.allocation[pid] = data;
        state.loading = 'succeeded';
      })
      .addCase(getAllocationHistory.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchIncomeModel.pending, (state) => {
        state.loading = 'loading';
      })
      .addCase(fetchIncomeModel.fulfilled, (state, action) => {
        state.incomeModels.push(action.payload);
        state.loading = 'succeeded';
      })
      .addCase(fetchIncomeModel.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      });
  },
});

export const { resetIncomeModels, removeIncomeModel, updateModelColor } = timeSeriesModelsSlice.actions;

export const selectAllocationHistory = createSelector(
  [(state, pid) => state.timeSeriesModels.allocation[pid]],
  (timeSeries) => timeSeries
);

export const selectIncomeModels = (state) => state.timeSeriesModels.incomeModels;

export default timeSeriesModelsSlice.reducer;